import {
  arrayOf,
  element,
  oneOfType,
  string,
  func,
  bool,
  object
} from 'prop-types';

import React from 'react';
import cx from 'classnames';
import { fromPairs, isNumber, isString, kebabCase, startCase } from 'lodash';

import { isBrokerage } from 'services/Products';
import { metaDataTypes } from 'services/TableSupport';
import AccountName from 'components/shared/Table/AccountName';
import BaseModal from 'components/shared/Modal/BaseModal';
import Toggle from 'components/shared/ToggleRP';
import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import Content from 'services/Content';
import { NON_BREAKING_SPACE, MONEY_MARKET_FORMAT } from 'services/Formats';
import {
  Dollar,
  EvaluatedDollar,
  EvaluatedPercent,
  FormattedDate,
  Quantity
} from 'components/shared/Text/Text';
import { ActionsList } from 'components/layouts/TableActionsModal/TableActionModal';
import AccountLabel from 'components/shared/AccountLabel/AccountLabel';
import { shareClassConversionEnabled } from 'services/FeatureFlags';
import { hasAccountLabel } from 'services/Accounts';

/**
 * A React component that is meant to disappear when rendered.
 * It can be when an entry is going to be rendered but shouldn't produce anything.
 *
 * @example
 * <tr><RowFillingTD /><Empty /></tr>
 * // => <tr><td colspan='2'>...</td>{null}</tr>
 *
 * @returns {null}
 * @constructor
 */
const Empty = () => null;

export const sortObjectByKeys = (object, comparator) => {
  const keys = Object.keys(object);
  const sortedKeys = comparator ? keys.sort(comparator) : keys;

  return fromPairs(sortedKeys.map((key) => [key, object[key]]));
};

const AccountNameWithPermissions = WithUserPermissions(AccountName);

export const labelCells = ({
  cellLabelClass,
  fullDetails,
  overrides = {}
}) => ({
  difference: () => 'Daily Change ($)',
  percentChange: () => 'Daily Change (%)',
  ticker: () => 'Symbol',
  cusip: () => 'Symbol',
  accountName: () => (
    <td className={cellLabelClass} colSpan={2}>
      <a>View AccountDetails</a>
    </td>
  ),
  accountWChecksum: () => 'Account Number',
  accountId: (n, v) =>
    fullDetails && fullDetails.ownershipType !== 'viewOnly' ? (
      <td className={cellLabelClass} colSpan={2}>
        <AccountNameWithPermissions
          productType={fullDetails.productType}
          accountId={v}
          {...fullDetails}
          shouldDisable={isBrokerage(fullDetails.productType)}
          accountName="View Details"
        />
      </td>
    ) : (
      <Empty />
    ),
  ownerNames: () => 'Owners',
  percentReturn: () => 'PRR Since Inception',
  inceptionDate: () => 'Since Inception Date',
  sweepAccountWChecksum: () => 'Linked Account',
  actions: () => (
    <Toggle>
      {({ on, setOn, setOff }) => (
        <td className={cellLabelClass} onClick={setOn} colSpan={2}>
          <a>Actions</a>
          <MobileActionModal
            close={(e) => (e && e.stopPropagation(), setOff())}
            isOpen={on}
            details={fullDetails}
          />
        </td>
      )}
    </Toggle>
  ),
  default: (v) => <td className={cellLabelClass}>{startCase(v)}</td>,
  ...overrides
});

export const valueCells = ({ cellValueClass, type, overrides = {} }) => ({
  /*
   * The AccountDetail component in the Positions table is
   * a wretched beast of conditionals resolving conflicting possible states.
   * We need to seriously think ahead before letting that rats nest of booleans to pollute this file
   * */
  accountName: Empty,

  accountId: Empty,

  actions: Empty,

  ownerNames: (ownerNames) => <td className={cellValueClass}>{ownerNames}</td>,

  asOfDate: (asOfDate, details) => (
    <div className={cellValueClass}>
      <FormattedDate>{asOfDate}</FormattedDate>
      {NON_BREAKING_SPACE}
      <DisclosureModal
        className="as-of-date-disclosure"
        content={
          isBrokerage(details.productType)
            ? Content.asOfDateDisclosureBrokerage
            : Content.asOfDateDisclosureAPP
        }
        title="Important Information Regarding the Updating of Account Balances"
        hideLinkContent
      />
    </div>
  ),

  accountLabel: (accountId) => (
    <td className={cellValueClass}>
      <AccountLabel accountId={accountId}/>
    </td>
  ),

  ticker: (ticker) => <td className={cellValueClass}>{ticker}</td>,

  quantity: (val) => (
    <Quantity
      element="td"
      extraDecimal={type.extraDecimal === metaDataTypes.SHOW}
      className={cellValueClass}>
      {val}
    </Quantity>
  ),

  price: (val, details) =>
    details.name.moneyMarket ? (
      <Dollar
        element="td"
        format={MONEY_MARKET_FORMAT}
        className={cellValueClass}>
        {val}
      </Dollar>
    ) : (
      <Dollar element="td" className={cellValueClass}>
        {val}
      </Dollar>
    ),
  change: (val, details) =>
    details.name.moneyMarket ? (
      <EvaluatedDollar
        element="td"
        format={MONEY_MARKET_FORMAT}
        className={cellValueClass}>
        {val}
      </EvaluatedDollar>
    ) : (
      <EvaluatedDollar element="td" className={cellValueClass}>
        {val}
      </EvaluatedDollar>
    ),
  difference: (v) => (
    <EvaluatedDollar element="td" className={cellValueClass}>
      {v}
    </EvaluatedDollar>
  ),

  percentChange: (v) => (
    <td className={cellValueClass}>
      <EvaluatedPercent
        key="daily-percent"
        className="e2e-daily-change-percent">
        {v}
      </EvaluatedPercent>
    </td>
  ),

  balance: (balance) => (
    <Dollar element="td" className={cellValueClass}>
      {isNumber(balance) && balance > 0 ? balance : 0}
    </Dollar>
  ),

  percentReturn: (v) => (
    <td className={cellValueClass}>
      <EvaluatedPercent
        key="daily-percent"
        className="e2e-daily-change-percent">
        {v}
      </EvaluatedPercent>
    </td>
  ),
  inceptionDate: (v) => (
    <td className={cellValueClass}>
      <FormattedDate>{v}</FormattedDate>
      {NON_BREAKING_SPACE}
      <DisclosureModal
        className="restricted-height-modal"
        content={Content.prrPerformanceDisclosure}
        hideLinkContent
      />
    </td>
  ),
  default: (
    v // Odds are this will be removed or replaced with Empty
  ) => <td className={cellValueClass}>{v}</td>,
  ...overrides
});

const MobileActionModal = ({ close, isOpen, details }) => {
  const { productType, accountId, accountName, accountWChecksum } =
    details.name;
  return (
    <BaseModal close={close} isOpen={isOpen} title={null} hideAnchor>
      <div className="mobile-modal-actions-header">{accountName}</div>
      <div className="mobile-modal-actions-subheader">{accountWChecksum}</div>
      <ActionsList
        classPrefix="mobile-modal-actions"
        productType={productType}
        accountId={accountId}
      />
    </BaseModal>
  );
};

MobileActionModal.propTypes = {
  close: func,
  isOpen: bool,
  details: object
};

const COL_LABEL_ACCOUNT = 'account';
const COL_LABEL_BALANCE = 'balance';

export const mobileColumns = [COL_LABEL_ACCOUNT, COL_LABEL_BALANCE];

export const TableColumns = ({ columns = mobileColumns, baseClass }) => (
  <thead>
    <tr className="columns">
      {columns.map((name) => (
        <th
          scope="col"
          className={cx(
            `${baseClass}-column-${kebabCase(name)}`,
            `e2e-${baseClass}-column-${kebabCase(name)}`,
            'column-header-cell'
          )}
          key={name}>
          {startCase(name)}
        </th>
      ))}
    </tr>
  </thead>
);

TableColumns.propTypes = {
  columns: arrayOf(oneOfType([element, string])),
  tableClass: func.isRequired
};

export const RowLabelCell = ({ children, className }) =>
  isString(children) ? <td className={className}>{children}</td> : children;

export const showAccountLabel = (activeAccount) => shareClassConversionEnabled() && hasAccountLabel(activeAccount);
